import type { Command } from 'commander';
import { unwrapDataResponse } from '@wport/core';
import { enterpriseGet } from '../../../lib/enterprise-client';
import { resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext } from '../../../lib/global-opts';
import { CliError, ExitCode } from '../../../lib/errors';
import { printJson, sanitizeForTerminal } from '../../../lib/output';
import { parseFieldsList, pickPaths } from '../../../lib/path-utils';
import { formatStatus } from './list';

interface ViewFlags {
	fields?: string;
}

interface EnterpriseJobDetail {
	enc_id?: string;
	job_title?: string | null;
	code?: string | null;
	status?: number;
	created_at?: string | null;
	updated_at?: string | null;
	[k: string]: unknown;
}

const DETAIL_FIELDS: ReadonlyArray<string> = ['enc_id', 'job_title', 'code', 'status', 'created_at', 'updated_at'];

function renderDetailLines(job: EnterpriseJobDetail): string[] {
	const pad = Math.max(...DETAIL_FIELDS.map((f) => f.length)) + 1;
	const lines: string[] = [];
	for (const field of DETAIL_FIELDS) {
		const raw = job[field];
		if (raw === null || raw === undefined) continue;
		const value = field === 'status' ? formatStatus(raw as number) : String(raw);
		lines.push(`${(field + ':').padEnd(pad + 1)}${sanitizeForTerminal(value)}`);
	}
	return lines;
}

export function registerEnterpriseJobsView(parent: Command): void {
	parent
		.command('view <enc_id>')
		.description('View one of your company job postings')
		.option('--fields <list>', 'output selected fields as JSON (comma-separated dotted paths)')
		.action(async (encId: string, flags: ViewFlags, command: Command) => {
			const ctx = resolveContext(command);
			if (!encId.trim()) {
				throw new CliError('enc_id must not be empty', ExitCode.InvalidArgument);
			}
			const globals = command.optsWithGlobals() as { apiKey?: string };
			const { key } = resolveApiKey(globals.apiKey);
			const { body } = await enterpriseGet(
				{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey: key },
				`/jobs/${encodeURIComponent(encId.trim())}`
			);
			const job = unwrapDataResponse<EnterpriseJobDetail>(body);

			if (flags.fields) {
				printJson(pickPaths(job, parseFieldsList(flags.fields)));
				return;
			}
			if (ctx.format === 'json') {
				printJson(job);
				return;
			}
			process.stdout.write(renderDetailLines(job).join('\n') + '\n');
		});
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = { renderDetailLines };
